<template>
  <div 
    class="yearbook-card" 
    :class="{ 'highlighted': isUserFaculty }"
    @click="openYearbook"
  >
    <!-- Book spine effect -->
    <div class="book-spine" :style="{ backgroundColor: yearbook.color }"></div>
    
    <!-- Book cover -->
    <div class="book-cover" :style="{ borderColor: yearbook.color }">
      <!-- Highlight badge for user's faculty -->
      <div v-if="isUserFaculty" class="faculty-badge">
        <span class="material-symbols-outlined">star</span>
        <span>Your Faculty</span>
      </div>

      <!-- Faculty icon -->
      <div class="book-icon" :style="{ backgroundColor: yearbook.color }">
        <span class="material-symbols-outlined">{{ yearbook.icon }}</span>
      </div>

      <!-- Book title -->
      <div class="book-title">
        <h3>{{ yearbook.name }}</h3>
        <p class="book-subtitle">Yearbook {{ yearbook.year }}</p>
      </div>

      <!-- Faculty code -->
      <div class="faculty-code" :style="{ color: yearbook.color }">
        {{ yearbook.code }}
      </div>

      <!-- Action buttons -->
      <div class="book-actions">
        <button 
          @click.stop="openYearbook(true)"
          class="action-btn primary"
          :style="{ backgroundColor: yearbook.color }"
        >
          <span class="material-symbols-outlined">menu_book</span>
          Full Content
        </button>
        <button 
          @click.stop="openYearbook(false)"
          class="action-btn secondary"
          :style="{ borderColor: yearbook.color, color: yearbook.color }"
        >
          <span class="material-symbols-outlined">summarize</span>
          Summary
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YearbookCard',
  props: {
    yearbook: {
      type: Object,
      required: true
    },
    isUserFaculty: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    openYearbook(fullContent = true) {
      const url = fullContent ? this.yearbook.fullUrl : this.yearbook.summaryUrl;
      window.open(url, '_blank');
      this.$emit('access', this.yearbook);
    }
  }
};
</script>

<style scoped>
.yearbook-card {
  position: relative;
  display: flex;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 320px;
  margin-bottom: 1rem;
}

.yearbook-card:hover {
  transform: translateY(-4px);
}

.yearbook-card.highlighted {
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(218, 170, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(218, 170, 0, 0.6);
  }
}

/* Book spine - creates 3D effect */
.book-spine {
  width: 20px;
  border-radius: 4px 0 0 4px;
  box-shadow: 
    inset -2px 0 4px rgba(0, 0, 0, 0.2),
    2px 0 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.book-spine::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.1), transparent);
}

/* Book cover */
.book-cover {
  flex: 1;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 3px solid;
  border-left: none;
  border-radius: 0 8px 8px 0;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 
    4px 4px 12px rgba(0, 0, 0, 0.1),
    inset -1px -1px 2px rgba(0, 0, 0, 0.05);
}

.book-cover::before {
  content: '';
  position: absolute;
  top: 12px;
  right: 12px;
  bottom: 12px;
  left: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  pointer-events: none;
}

/* Faculty badge */
.faculty-badge {
  position: absolute;
  top: -8px;
  right: 16px;
  background: linear-gradient(135deg, #DAAA00 0%, #f59e0b 100%);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(218, 170, 0, 0.3);
  z-index: 10;
}

.faculty-badge .material-symbols-outlined {
  font-size: 16px;
}

/* Book icon */
.book-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.book-icon .material-symbols-outlined {
  font-size: 32px;
  color: white;
}

/* Book title */
.book-title {
  text-align: center;
  margin-bottom: 1rem;
  flex: 1;
}

.book-title h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.book-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

/* Faculty code */
.faculty-code {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  opacity: 0.8;
}

/* Action buttons */
.book-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
}

.action-btn {
  flex: 1;
  padding: 0.625rem 0.75rem;
  border: 2px solid transparent;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}

.action-btn .material-symbols-outlined {
  font-size: 18px;
}

.action-btn.primary {
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.action-btn.secondary {
  background: white;
  border: 2px solid;
}

.action-btn.secondary:hover {
  background: rgba(0, 0, 0, 0.02);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .yearbook-card {
    height: 280px;
  }

  .book-title h3 {
    font-size: 1rem;
  }

  .faculty-code {
    font-size: 1.25rem;
  }

  .action-btn {
    font-size: 0.75rem;
    padding: 0.5rem;
  }

  .action-btn span:not(.material-symbols-outlined) {
    display: none;
  }
}
</style>
